import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, Maximize2 } from 'lucide-react';

const ACCENTS = {
  emerald: { ring: 'ring-emerald-500', border: 'border-emerald-500', text: 'text-emerald-500', badge: 'bg-emerald-500' },
  blue: { ring: 'ring-blue-500', border: 'border-blue-500', text: 'text-blue-500', badge: 'bg-blue-500' },
  cyan: { ring: 'ring-cyan-500', border: 'border-cyan-500', text: 'text-cyan-500', badge: 'bg-cyan-500' },
};

const ProductGallery = ({ images = [], accent = 'emerald', badge, testid = 'product-gallery' }) => {
  const a = ACCENTS[accent] || ACCENTS.emerald;
  const [active, setActive] = useState(0);
  const [fade, setFade] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState('50% 50%');
  const mainRef = useRef(null);
  const touchStart = useRef(null);

  const hasMultiple = images.length > 1;
  const current = images[active] || {};

  const select = useCallback((i) => {
    if (i === active) return;
    setFade(true);
    setTimeout(() => {
      setActive(((i % images.length) + images.length) % images.length);
      setFade(false);
    }, 160);
  }, [active, images.length]);

  const next = useCallback(() => select(active + 1), [active, select]);
  const prev = useCallback(() => select(active - 1), [active, select]);

  // Desktop hover-to-zoom (transform-origin follows cursor)
  const onMove = (e) => {
    if (lightbox) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  // Keyboard nav in lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, next, prev]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null || zoomed) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next() : prev());
    touchStart.current = null;
  };

  return (
    <div data-testid={testid} className="min-w-0 max-w-full">
      <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 min-w-0">
        {/* Thumbnails */}
        {hasMultiple && (
          <div
            className="flex sm:flex-col gap-2.5 sm:w-20 lg:w-24 overflow-x-auto sm:overflow-y-auto sm:max-h-[460px] pb-1 sm:pb-0 pr-1 scrollbar-thin"
            data-testid="gallery-thumbs"
          >
            {images.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => select(i)}
                data-testid={`gallery-thumb-${i}`}
                aria-label={`View image ${i + 1}`}
                className={`flex-shrink-0 w-16 h-16 sm:w-full sm:h-20 rounded-xl overflow-hidden bg-white border-2 transition-all duration-200 ${
                  i === active ? `${a.border} ring-2 ${a.ring} ring-offset-1` : 'border-white/40 hover:border-white/80'
                }`}
              >
                <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-contain" />
              </button>
            ))}
          </div>
        )}

        {/* Main image */}
        <div className="flex-1 min-w-0">
          <div
            ref={mainRef}
            onMouseMove={onMove}
            onMouseLeave={() => setOrigin('50% 50%')}
            onClick={() => setLightbox(true)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="gallery-main"
            className="group relative aspect-[4/3] w-full rounded-2xl overflow-hidden bg-[#f5f6f8] shadow-2xl cursor-zoom-in select-none"
          >
            <img
              src={current.src}
              alt={current.alt}
              fetchPriority={active === 0 ? 'high' : 'auto'}
              className={`w-full h-full object-contain transition-[opacity,transform] duration-300 ease-out will-change-transform sm:group-hover:scale-[1.8] ${fade ? 'opacity-0' : 'opacity-100'}`}
              style={{ transformOrigin: origin }}
            />
            {badge && (
              <div className={`absolute top-4 right-4 ${a.badge} text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg pointer-events-none`}>
                {badge}
              </div>
            )}
            {/* Zoom hint */}
            <div className="absolute bottom-4 right-4 bg-black/55 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <Maximize2 className="w-4 h-4" />
            </div>
            <div className="absolute bottom-4 left-4 bg-white/85 backdrop-blur text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full pointer-events-none">
              {active + 1} / {images.length}
            </div>
            {/* Desktop arrows */}
            {hasMultiple && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  aria-label="Previous image"
                  className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 items-center justify-center w-9 h-9 rounded-full bg-white/80 text-gray-800 shadow hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  aria-label="Next image"
                  className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 items-center justify-center w-9 h-9 rounded-full bg-white/80 text-gray-800 shadow hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
          <p className="mt-3 text-center text-xs text-white/60 flex items-center justify-center gap-1.5">
            <ZoomIn className="w-3.5 h-3.5" /> Hover to zoom · Click to view fullscreen
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col animate-in fade-in duration-200"
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(false)}
        >
          <div className="flex items-center justify-between px-5 py-4 text-white/90">
            <span className="text-sm font-medium">{active + 1} / {images.length}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
              aria-label="Close"
              data-testid="lightbox-close"
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div
            className="flex-1 flex items-center justify-center relative overflow-hidden px-4"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {hasMultiple && (
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                data-testid="lightbox-prev"
                className="absolute left-3 sm:left-6 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <img
              src={current.src}
              alt={current.alt}
              onClick={() => setZoomed((z) => !z)}
              style={{ touchAction: 'pinch-zoom', transformOrigin: origin }}
              onMouseMove={zoomed ? onMove : undefined}
              className={`max-h-[78vh] max-w-[92vw] object-contain rounded-lg transition-transform duration-300 ${zoomed ? 'scale-[2] cursor-zoom-out' : 'cursor-zoom-in'}`}
            />
            {hasMultiple && (
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                data-testid="lightbox-next"
                className="absolute right-3 sm:right-6 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Lightbox thumbnails */}
          {hasMultiple && (
            <div className="px-4 py-4 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex gap-2 justify-center min-w-max mx-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setZoomed(false); setActive(i); }}
                    className={`w-14 h-14 rounded-lg overflow-hidden bg-white/90 border-2 transition-all ${i === active ? a.border : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
